import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import type { SignalState } from "../api/client";
import { stateView } from "../signal-state";

/** Signal: LED + source code (+ caption). The mockup's signature motif. */
@customElement("ld-signal")
export class LdSignal extends LitElement {
  @property() code = "";
  @property() state: SignalState | null = null;
  /** Text under the code; defaults to the state's label. */
  @property() caption?: string;
  @property({ type: Boolean, reflect: true }) compact = false;

  static styles = css`
    :host { display: inline-flex; }
    .signal {
      display: flex; align-items: center; gap: 9px; padding: 9px 14px 9px 10px;
      border-radius: var(--r-pill); border: 1px solid var(--slate-200); background: var(--white);
      transition: transform .18s var(--ease), box-shadow .18s var(--ease);
    }
    .signal:hover { transform: translateY(-1px); box-shadow: var(--shadow-sm); }
    :host([compact]) .signal { padding: 0; border: none; background: none; gap: 0; }
    :host([compact]) .signal:hover { transform: none; box-shadow: none; }
    .led {
      width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
      background: var(--neutral); box-shadow: 0 0 0 3px var(--neutral-bg);
    }
    :host([compact]) .led { width: 9px; height: 9px; }
    [data-tone="green"] .led { background: var(--green); box-shadow: 0 0 0 3px var(--green-bg), 0 0 8px rgba(26,162,96,.45); }
    [data-tone="amber"] .led { background: var(--amber); box-shadow: 0 0 0 3px var(--amber-bg), 0 0 8px rgba(201,121,10,.4); }
    [data-tone="red"] .led { background: var(--red); box-shadow: 0 0 0 3px var(--red-bg), 0 0 8px rgba(214,64,58,.4); }
    .text { display: flex; flex-direction: column; line-height: 1.15; }
    .code { font-family: var(--font-mono); font-size: 11.5px; font-weight: 600; letter-spacing: .03em; color: var(--ink); }
    .caption { font-size: 10.5px; color: var(--slate-400); font-weight: 500; }
  `;

  render() {
    const view = this.state ? stateView(this.state) : null;
    const caption = this.caption ?? view?.label ?? "";
    const title = view ? `${this.code} — ${view.label}` : this.code;
    return html`
      <span class="signal" data-tone=${view?.tone ?? "neutral"} title=${title} role="img" aria-label=${title}>
        <span class="led"></span>
        ${this.compact
          ? nothing
          : html`<span class="text"><span class="code">${this.code}</span><span class="caption">${caption}</span></span>`}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ld-signal": LdSignal;
  }
}
